<template>
  <div class="dashboard">
    <!-- 매출 요약 카드 -->
    <div class="summary-cards">
      <div
        v-for="(item, index) in summaryItems"
        :key="index"
        class="summary-card"
        :class="{ active: !isCustomPeriod && searchForm.periodType === item.type }"
        @click="selectPeriod(item.type)">
        <div class="card-header">
          <i :class="item.icon"></i>
          <span>{{ item.label }}</span>
        </div>
        <div class="card-value">{{ formatCurrency(item.value) }}</div>
        <div class="card-footer">
          <span class="period-badge" :class="{ selected: !isCustomPeriod && searchForm.periodType === item.type }">
            {{ !isCustomPeriod && searchForm.periodType === item.type ? '선택됨' : '' }}
          </span>
        </div>
      </div>

      <!-- 상세 조회 버튼 카드 -->
      <div class="summary-card filter-card" :class="{ active: showAdvancedFilter }" @click="toggleAdvancedFilter">
        <div class="card-header">
          <i class="el-icon-s-operation"></i>
          <span>{{ $t('상세 조회') }}</span>
        </div>
        <div class="card-value filter-icon">
          <i :class="showAdvancedFilter ? 'el-icon-arrow-up' : 'el-icon-arrow-down'"></i>
        </div>
        <div class="card-footer">
          <span class="period-badge" :class="{ selected: isCustomPeriod }">
            {{ isCustomPeriod ? '사용자 지정' : '' }}
          </span>
        </div>
      </div>
    </div>

    <!-- 상세 조회 패널 -->
    <transition name="slide">
      <div v-if="showAdvancedFilter" class="advanced-filter">
        <el-row :gutter="20">
          <el-col :xs="24" :md="12">
            <div class="filter-group">
              <div class="filter-label">
                <i class="el-icon-date"></i>
                {{ $t('조회 기간') }}
              </div>
              <el-date-picker
                v-model="customDateRange"
                type="daterange"
                :range-separator="$t('~')"
                :start-placeholder="$t('시작일')"
                :end-placeholder="$t('종료일')"
                :size="size"
                value-format="yyyy-MM-dd"
                @change="onCustomDateChange"
                style="width: 100%">
              </el-date-picker>
            </div>
          </el-col>
          <el-col :xs="24" :md="12">
            <div class="filter-group">
              <div class="filter-label">
                <el-checkbox v-model="compareMode" @change="onCompareModeChange">
                  <i class="el-icon-sort"></i>
                  {{ $t('기간 비교') }}
                </el-checkbox>
              </div>
              <div v-if="compareMode" class="compare-options">
                <el-radio-group v-model="compareType" :size="size" @change="onCompareTypeChange">
                  <el-radio-button label="PREV_YEAR">{{ $t('전년 동기') }}</el-radio-button>
                  <el-radio-button label="PREV_MONTH">{{ $t('전월 대비') }}</el-radio-button>
                  <el-radio-button label="PREV_PERIOD">{{ $t('이전 기간') }}</el-radio-button>
                  <el-radio-button label="CUSTOM">{{ $t('직접 선택') }}</el-radio-button>
                </el-radio-group>
              </div>
            </div>
          </el-col>
        </el-row>

        <el-row v-if="compareMode && compareType === 'CUSTOM'" :gutter="20" style="margin-top: 15px">
          <el-col :xs="24" :md="12">
            <div class="filter-group">
              <div class="filter-label">
                <i class="el-icon-date"></i>
                {{ $t('비교 기간') }}
              </div>
              <el-date-picker
                v-model="compareDateRange"
                type="daterange"
                :range-separator="$t('~')"
                :start-placeholder="$t('시작일')"
                :end-placeholder="$t('종료일')"
                :size="size"
                value-format="yyyy-MM-dd"
                style="width: 100%">
              </el-date-picker>
            </div>
          </el-col>
        </el-row>

        <!-- 비교 기간 표시 -->
        <div v-if="compareMode && computedCompareRange" class="compare-info">
          <div class="compare-period current">
            <span class="label">조회 기간:</span>
            <span class="value">{{ customDateRange ? customDateRange[0] + ' ~ ' + customDateRange[1] : '-' }}</span>
          </div>
          <div class="compare-period compare">
            <span class="label">비교 기간:</span>
            <span class="value">{{ computedCompareRange[0] + ' ~ ' + computedCompareRange[1] }}</span>
          </div>
        </div>

        <div class="filter-actions">
          <el-button :size="size" @click="resetFilter" icon="el-icon-refresh">
            {{ $t('초기화') }}
          </el-button>
          <el-button type="primary" :size="size" @click="searchCustomPeriod" icon="el-icon-search">
            {{ $t('조회') }}
          </el-button>
        </div>
      </div>
    </transition>

    <!-- 비교 결과 요약 (비교 모드일 때만) -->
    <div v-if="compareMode && compareData" class="compare-summary">
      <el-row :gutter="20">
        <el-col :xs="24" :sm="8">
          <div class="compare-card">
            <div class="compare-card-header">총 매출</div>
            <div class="compare-card-body">
              <div class="current-value">{{ formatCurrency(currentTotalAmount) }}</div>
              <div class="compare-value">
                <span class="label">비교:</span>
                {{ formatCurrency(compareTotalAmount) }}
              </div>
              <div class="diff-value" :class="getDiffClass(currentTotalAmount, compareTotalAmount)">
                <i :class="getDiffIcon(currentTotalAmount, compareTotalAmount)"></i>
                {{ formatDiffPercent(currentTotalAmount, compareTotalAmount) }}
              </div>
            </div>
          </div>
        </el-col>
        <el-col :xs="24" :sm="8">
          <div class="compare-card">
            <div class="compare-card-header">총 건수</div>
            <div class="compare-card-body">
              <div class="current-value">{{ formatNumber(currentTotalCount) }}건</div>
              <div class="compare-value">
                <span class="label">비교:</span>
                {{ formatNumber(compareTotalCount) }}건
              </div>
              <div class="diff-value" :class="getDiffClass(currentTotalCount, compareTotalCount)">
                <i :class="getDiffIcon(currentTotalCount, compareTotalCount)"></i>
                {{ formatDiffPercent(currentTotalCount, compareTotalCount) }}
              </div>
            </div>
          </div>
        </el-col>
        <el-col :xs="24" :sm="8">
          <div class="compare-card">
            <div class="compare-card-header">건당 평균</div>
            <div class="compare-card-body">
              <div class="current-value">{{ formatCurrency(currentAvgAmount) }}</div>
              <div class="compare-value">
                <span class="label">비교:</span>
                {{ formatCurrency(compareAvgAmount) }}
              </div>
              <div class="diff-value" :class="getDiffClass(currentAvgAmount, compareAvgAmount)">
                <i :class="getDiffIcon(currentAvgAmount, compareAvgAmount)"></i>
                {{ formatDiffPercent(currentAvgAmount, compareAvgAmount) }}
              </div>
            </div>
          </div>
        </el-col>
      </el-row>
    </div>

    <!-- 그래프 차트 (기간 조회 시) -->
    <div v-if="graphData.length > 0 || (compareMode && compareGraphData.length > 0)" class="graph-section">
      <div class="chart-card">
        <div class="chart-header">
          <i class="el-icon-data-analysis"></i>
          <span>{{ $t('매출 추이') }}</span>
          <span v-if="isCustomPeriod" class="period-info">
            ({{ customDateRange ? customDateRange[0] + ' ~ ' + customDateRange[1] : '' }})
          </span>
        </div>
        <div ref="graphChart" class="graph-container"></div>
      </div>
    </div>

    <!-- 차트 영역 -->
    <el-row :gutter="20" class="chart-section">
      <el-col :xs="24" :sm="24" :md="8">
        <div class="chart-card">
          <div class="chart-header">
            <i class="el-icon-s-order"></i>
            <span>{{ $t('주문 유형별') }}</span>
          </div>
          <div ref="orderTypeChart" class="chart-container"></div>
        </div>
      </el-col>
      <el-col :xs="24" :sm="24" :md="8">
        <div class="chart-card">
          <div class="chart-header">
            <i class="el-icon-bank-card"></i>
            <span>{{ $t('결제 수단별') }}</span>
          </div>
          <div ref="paymentTypeChart" class="chart-container"></div>
        </div>
      </el-col>
      <el-col :xs="24" :sm="24" :md="8">
        <div class="chart-card">
          <div class="chart-header">
            <i class="el-icon-data-line"></i>
            <span>{{ $t('기간별 매출') }}</span>
          </div>
          <div ref="periodChart" class="chart-container"></div>
        </div>
      </el-col>
    </el-row>

    <!-- 상세 테이블 영역 -->
    <el-row :gutter="20" class="table-section">
      <el-col :xs="24" :md="12">
        <div class="table-card">
          <div class="table-header">
            <i class="el-icon-s-order"></i>
            <span>{{ $t('주문 유형별 상세') }}</span>
            <SalesBasisInfo basis="net" class="header-basis-info" />
          </div>
          <el-table
            :data="orderTpList"
            :size="size"
            :empty-text="$t('데이타 없음')"
            max-height="280"
            row-key="key"
            :tree-props="{ children: 'children' }">
            <el-table-column :label="$t('유형')" prop="name" min-width="100">
              <template slot-scope="scope">
                <!-- 배달 ▸ 앱 ▸ 가게 — 가게 줄은 색 배지 없이 글자로(`utils/deliveryShops.deliveryOrderTypeRows`) -->
                <span v-if="scope.row.shop" class="shop-name" :class="{ 'shop-name--unknown': scope.row.unknown }">
                  {{ scope.row.unknown ? $t(scope.row.name) : scope.row.name }}
                </span>
                <span v-else class="type-badge" :style="{ backgroundColor: scope.row.color }">
                  {{ scope.row.name }}
                </span>
              </template>
            </el-table-column>
            <el-table-column :label="$t('금액')" prop="value" min-width="120" align="right">
              <template slot-scope="scope">
                <div>{{ formatCurrency(scope.row.value) }}</div>
                <!-- 병기(참고) 지표: 정산 데이터가 수집된 주문의 기준매출(gross) — 본 지표(실결제)와 기준이 다름 -->
                <div v-if="scope.row.settledCount > 0" class="settled-ref">
                  {{ $t('정산 기준매출') }} {{ formatCurrency(scope.row.settledGross) }} · {{ scope.row.settledCount
                  }}{{ $t('건') }}
                </div>
              </template>
            </el-table-column>
            <el-table-column :label="$t('비율')" prop="percent" width="80" align="right">
              <template slot-scope="scope">{{ scope.row.percent }}%</template>
            </el-table-column>
            <el-table-column v-if="compareMode" :label="$t('비교')" width="100" align="right">
              <template slot-scope="scope">
                <span :class="getDiffClass(scope.row.value, scope.row.compareValue)">
                  {{ formatDiffPercent(scope.row.value, scope.row.compareValue) }}
                </span>
              </template>
            </el-table-column>
          </el-table>
        </div>
      </el-col>
      <el-col :xs="24" :md="12">
        <div class="table-card">
          <div class="table-header">
            <i class="el-icon-bank-card"></i>
            <span>{{ $t('결제 수단별 상세') }}</span>
          </div>
          <el-table :data="salesTpList" :size="size" :empty-text="$t('데이타 없음')" max-height="280">
            <el-table-column :label="$t('수단')" prop="name" min-width="100">
              <template slot-scope="scope">
                <span class="type-badge" :style="{ backgroundColor: scope.row.color }">
                  {{ scope.row.name }}
                </span>
              </template>
            </el-table-column>
            <el-table-column :label="$t('금액')" prop="value" min-width="120" align="right">
              <template slot-scope="scope">{{ formatCurrency(scope.row.value) }}</template>
            </el-table-column>
            <el-table-column :label="$t('비율')" prop="percent" width="80" align="right">
              <template slot-scope="scope">{{ scope.row.percent }}%</template>
            </el-table-column>
            <el-table-column v-if="compareMode" :label="$t('비교')" width="100" align="right">
              <template slot-scope="scope">
                <span :class="getDiffClass(scope.row.value, scope.row.compareValue)">
                  {{ formatDiffPercent(scope.row.value, scope.row.compareValue) }}
                </span>
              </template>
            </el-table-column>
          </el-table>
        </div>
      </el-col>
    </el-row>

    <!-- 인기 순위 -->
    <el-row :gutter="20" class="ranking-section">
      <el-col :xs="24" :md="8">
        <div class="ranking-card">
          <div class="ranking-header">
            <i class="el-icon-trophy"></i>
            <span>{{ $t('인기 상품 TOP 10') }}</span>
          </div>
          <div class="ranking-list">
            <div v-for="(item, index) in topSmenus" :key="index" class="ranking-item">
              <span class="rank" :class="getRankClass(index)">{{ index + 1 }}</span>
              <span class="name" :title="item.name">{{ item.name }}</span>
              <span class="count">{{ formatNumber(item.count) }}개</span>
              <span class="amount">{{ formatCurrency(item.amount) }}</span>
            </div>
            <div v-if="topSmenus.length === 0" class="empty-ranking">{{ $t('데이타 없음') }}</div>
          </div>
        </div>
      </el-col>
      <el-col :xs="24" :md="8">
        <div class="ranking-card">
          <div class="ranking-header">
            <i class="el-icon-folder"></i>
            <span>{{ $t('인기 카테고리 TOP 10') }}</span>
          </div>
          <div class="ranking-list">
            <div v-for="(item, index) in topCategories" :key="index" class="ranking-item">
              <span class="rank" :class="getRankClass(index)">{{ index + 1 }}</span>
              <span class="name" :title="item.name">{{ item.name }}</span>
              <span class="count">{{ formatNumber(item.count) }}개</span>
              <span class="amount">{{ formatCurrency(item.amount) }}</span>
            </div>
            <div v-if="topCategories.length === 0" class="empty-ranking">{{ $t('데이타 없음') }}</div>
          </div>
        </div>
      </el-col>
      <el-col :xs="24" :md="8">
        <div class="ranking-card">
          <div class="ranking-header">
            <i class="el-icon-s-operation"></i>
            <span>{{ $t('인기 옵션 TOP 10') }}</span>
          </div>
          <div class="ranking-list">
            <div v-for="(item, index) in topSmenuOpts" :key="index" class="ranking-item">
              <span class="rank" :class="getRankClass(index)">{{ index + 1 }}</span>
              <span class="name" :title="item.name">{{ item.name }}</span>
              <span class="count">{{ formatNumber(item.count) }}개</span>
              <span class="amount">{{ formatCurrency(item.amount) }}</span>
            </div>
            <div v-if="topSmenuOpts.length === 0" class="empty-ranking">{{ $t('데이타 없음') }}</div>
          </div>
        </div>
      </el-col>
    </el-row>
  </div>
</template>

<script>
import { logHttpFailure } from '@/plugins/http'
import { initData } from '@/utils/constants'
import { deliveryOrderTypeRows } from '@/utils/deliveryShops'
import { computeCompareRange, getDateRangeForPeriod } from '@/utils/periodFilter'
import SalesBasisInfo from '@/components/SalesBasisInfo.vue'

export default {
  components: { SalesBasisInfo },
  data() {
    const data = initData()
    data.searchForm = this.initSearchForm()

    data.periods = []
    data.orderTps = []
    data.salesTps = []
    data.deliveryTps = []
    data.deliveryShops = []
    data.topSmenus = []
    data.topCategories = []
    data.topSmenuOpts = []
    data.graphData = []

    data.orderTpList = []
    data.salesTpList = []

    // 비교 모드 데이터
    data.compareData = null
    data.compareGraphData = []

    data.orderTypeChart = null
    data.paymentTypeChart = null
    data.periodChart = null
    data.graphChart = null

    // 상세 조회 관련
    data.showAdvancedFilter = false
    data.customDateRange = null
    data.isCustomPeriod = false

    // 비교 모드 관련
    data.compareMode = false
    data.compareType = 'PREV_YEAR'
    data.compareDateRange = null

    // 사이트 톤에 맞는 색상 팔레트
    data.chartColors = [
      '#F27438', // primary orange
      '#52C41A', // green
      '#FAAD14', // amber
      '#FF4D4F', // red
      '#36CFC9', // teal
      '#7C5CFC', // purple
      '#FF85C0', // pink
      '#8C8CA1', // gray
    ]

    return data
  },
  computed: {
    summaryItems() {
      const periodData = this.periods[0] || {}
      return [
        {
          type: 'TODAY',
          label: this.$t('오늘'),
          value: periodData.today || 0,
          icon: 'el-icon-sunny',
        },
        {
          type: 'YESTERDAY',
          label: this.$t('어제'),
          value: periodData.yesterday || 0,
          icon: 'el-icon-time',
        },
        {
          type: 'THIS_WEEK',
          label: this.$t('이번주'),
          value: periodData.this_week || 0,
          icon: 'el-icon-date',
        },
        {
          type: 'THIS_MONTH',
          label: this.$t('이번달'),
          value: periodData.this_month || 0,
          icon: 'el-icon-s-data',
        },
        {
          type: 'LAST_MONTH',
          label: this.$t('지난달'),
          value: periodData.last_month || 0,
          icon: 'el-icon-d-arrow-left',
        },
      ]
    },
    /** 비교 기간 — 셈은 `utils/periodFilter.computeCompareRange` 한 곳(통합 매출과 같은 함수, KST 달력 날짜 셈) */
    computedCompareRange() {
      if (!this.compareMode) return null

      return computeCompareRange(this.customDateRange, this.compareType, this.compareDateRange)
    },
    currentTotalAmount() {
      const salesSum = this.salesTps[0] || {}
      return salesSum.total_amount || 0
    },
    currentTotalCount() {
      const salesSum = this.salesTps[0] || {}
      return salesSum.total_count || 0
    },
    currentAvgAmount() {
      if (this.currentTotalCount === 0) return 0
      return Math.round(this.currentTotalAmount / this.currentTotalCount)
    },
    compareTotalAmount() {
      if (!this.compareData) return 0
      const salesSum = this.compareData.salesSums?.[0] || {}
      return salesSum.total_amount || 0
    },
    compareTotalCount() {
      if (!this.compareData) return 0
      const salesSum = this.compareData.salesSums?.[0] || {}
      return salesSum.total_count || 0
    },
    compareAvgAmount() {
      if (this.compareTotalCount === 0) return 0
      return Math.round(this.compareTotalAmount / this.compareTotalCount)
    },
  },
  created() {
    this.searchWithGraph(this.searchForm.periodType)
  },
  mounted() {
    window.addEventListener('resize', this.handleResize)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize)
    if (this.orderTypeChart) this.orderTypeChart.dispose()
    if (this.paymentTypeChart) this.paymentTypeChart.dispose()
    if (this.periodChart) this.periodChart.dispose()
    if (this.graphChart) this.graphChart.dispose()
  },
  methods: {
    initSearchForm() {
      return {
        periodType: 'TODAY',
      }
    },
    selectPeriod(periodType) {
      this.isCustomPeriod = false
      this.compareMode = false
      this.compareData = null
      this.compareGraphData = []
      this.searchForm.periodType = periodType
      this.searchWithGraph(periodType)
    },
    toggleAdvancedFilter() {
      this.showAdvancedFilter = !this.showAdvancedFilter
      if (this.showAdvancedFilter && !this.customDateRange) {
        // 기본 날짜 범위 설정 (이번 달 — KST 달력, 프리셋과 같은 한 곳)
        const { startDate, endDate } = getDateRangeForPeriod('THIS_MONTH')
        this.customDateRange = [startDate, endDate]
      }
    },
    onCustomDateChange() {
      // 날짜 변경 시 자동 조회하지 않음 (조회 버튼 클릭 시 조회)
    },
    onCompareModeChange() {
      if (!this.compareMode) {
        this.compareData = null
        this.compareGraphData = []
      }
    },
    onCompareTypeChange() {
      // 비교 타입 변경 시 자동 조회하지 않음
    },
    resetFilter() {
      this.customDateRange = null
      this.compareMode = false
      this.compareType = 'PREV_YEAR'
      this.compareDateRange = null
      this.isCustomPeriod = false
      this.compareData = null
      this.compareGraphData = []
      this.searchForm.periodType = 'TODAY'
      this.searchWithGraph('TODAY')
    },
    async searchCustomPeriod() {
      if (!this.customDateRange) {
        this.$message.warning('조회 기간을 선택해주세요.')
        return
      }

      this.isCustomPeriod = true

      try {
        // 메인 기간 조회
        const result = await this.$axios
          .get('/statistics/summation', {
            params: {
              periodType: 'PERIOD',
              startDate: this.customDateRange[0],
              endDate: this.customDateRange[1],
            },
          })
          .then((res) => res.data)

        this.applySummation(result)

        // 비교 기간 조회
        if (this.compareMode && this.computedCompareRange) {
          const compareResult = await this.$axios
            .get('/statistics/summation', {
              params: {
                periodType: 'PERIOD',
                startDate: this.computedCompareRange[0],
                endDate: this.computedCompareRange[1],
              },
            })
            .then((res) => res.data)

          this.compareData = compareResult
          this.compareGraphData = compareResult.graph || []
        } else {
          this.compareData = null
          this.compareGraphData = []
        }

        this.buildOrderTpList()
        this.buildSalesTpList()

        this.$nextTick(() => {
          this.initCharts()
          if (this.graphData.length > 0 || this.compareGraphData.length > 0) {
            this.initGraphChart()
          }
        })
      } catch (error) {
        logHttpFailure(error, 'dashboard load failed: ')
        this.$message.error('조회 중 오류가 발생했습니다.')
      }
    },
    formatCurrency(value) {
      if (!value && value !== 0) return '₩0'
      return '₩' + Number(value).toLocaleString('ko-KR')
    },
    formatNumber(value) {
      if (!value && value !== 0) return '0'
      return Number(value).toLocaleString('ko-KR')
    },
    formatDiffPercent(current, compare) {
      if (!compare || compare === 0) {
        if (current > 0) return '+100%'
        return '0%'
      }
      const diff = ((current - compare) / compare) * 100
      const sign = diff >= 0 ? '+' : ''
      return `${sign}${diff.toFixed(1)}%`
    },
    getDiffClass(current, compare) {
      if (current > compare) return 'positive'
      if (current < compare) return 'negative'
      return 'neutral'
    },
    getDiffIcon(current, compare) {
      if (current > compare) return 'el-icon-top'
      if (current < compare) return 'el-icon-bottom'
      return 'el-icon-minus'
    },
    getRankClass(index) {
      if (index === 0) return 'gold'
      if (index === 1) return 'silver'
      if (index === 2) return 'bronze'
      return ''
    },
    handleResize() {
      if (this.orderTypeChart) this.orderTypeChart.resize()
      if (this.paymentTypeChart) this.paymentTypeChart.resize()
      if (this.periodChart) this.periodChart.resize()
      if (this.graphChart) this.graphChart.resize()
    },
    async search() {
      try {
        const result = await this.$axios
          .get('/statistics/summation', {
            params: {
              periodType: this.searchForm.periodType,
            },
          })
          .then((res) => res.data)

        this.applySummation(result)

        this.buildOrderTpList()
        this.buildSalesTpList()

        this.$nextTick(() => {
          this.initCharts()
          if (this.graphData.length > 0) {
            this.initGraphChart()
          }
        })
      } catch (error) {
        logHttpFailure(error, 'dashboard load failed: ')
      }
    },
    async searchWithGraph(periodType) {
      try {
        // 기간별 날짜 범위 계산
        const dateRange = getDateRangeForPeriod(periodType)

        // PERIOD 타입으로 API 호출하여 그래프 데이터 포함
        const result = await this.$axios
          .get('/statistics/summation', {
            params: {
              periodType: 'PERIOD',
              startDate: dateRange.startDate,
              endDate: dateRange.endDate,
            },
          })
          .then((res) => res.data)

        this.applySummation(result)

        this.buildOrderTpList()
        this.buildSalesTpList()

        this.$nextTick(() => {
          this.initCharts()
          if (this.graphData.length > 0) {
            this.initGraphChart()
          }
        })
      } catch (error) {
        logHttpFailure(error, 'dashboard load failed: ')
      }
    },
    /** 매출 합계 응답을 화면 상태로 — 세 조회 길(`search`·`searchWithGraph`·`searchCustomPeriod`)이 함께 쓴다 */
    applySummation(result) {
      this.bindPeriods(result.periods)
      this.orderTps = this.bindOrderTps(result.orderTps)
      this.salesTps = result.salesSums || []
      this.deliveryTps = result.deliveryTps || []
      this.deliveryShops = result.deliveryShops || []
      this.topSmenus = result.topSmenus || []
      this.topCategories = result.topCategories || []
      this.topSmenuOpts = result.topSmenuOpts || []
      this.graphData = result.graph || []
    },
    bindPeriods(data) {
      if (!data) return

      let today = 0,
        yesterday = 0,
        this_week = 0,
        this_month = 0,
        last_month = 0

      data.forEach((period) => {
        const pt = period.type
        if (pt === 'TODAY') today = period.amount
        if (pt === 'YESTERDAY') yesterday = period.amount
        if (pt === 'THIS_WEEK') this_week = period.amount
        if (pt === 'THIS_MONTH') this_month = period.amount
        if (pt === 'LAST_MONTH') last_month = period.amount
      })

      this.periods = [
        {
          today,
          yesterday,
          this_week,
          this_month,
          last_month,
        },
      ]
    },
    bindOrderTps(data) {
      if (!data) return []

      let pos = 0,
        kiosk = 0,
        table = 0,
        delivery_app = 0,
        telephone = 0,
        partner = 0,
        mobile_table = 0,
        etc = 0

      data.forEach((orderTp) => {
        const ot = orderTp.type
        if (ot === 'POS_ORDER') pos = orderTp.amount
        if (ot === 'KIOSK_ORDER') kiosk = orderTp.amount
        if (ot === 'TABLE_ORDER') table = orderTp.amount
        if (ot === 'DELIVERY_APP_ORDER') delivery_app = orderTp.amount
        if (ot === 'TELEPHONE_ORDER') telephone = orderTp.amount
        if (ot === 'PARTNER_ORDER') partner = orderTp.amount
        if (ot === 'MOBILE_TABLE_ORDER') mobile_table = orderTp.amount
        if (ot === 'ETC_ORDER') etc = orderTp.amount
      })

      return [{ pos, kiosk, table, delivery_app, telephone, partner, mobile_table, etc }]
    },
    buildOrderTpList() {
      const orderData = this.orderTps[0] || {}
      const compareOrderData = this.compareData ? this.bindOrderTps(this.compareData.orderTps)[0] || {} : {}

      const items = [
        { key: 'pos', name: this.$t('포스'), value: orderData.pos || 0 },
        { key: 'kiosk', name: this.$t('키오스크'), value: orderData.kiosk || 0 },
        { key: 'table', name: this.$t('오더톡'), value: orderData.table || 0 },
        { key: 'delivery_app', name: this.$t('배달'), value: orderData.delivery_app || 0 },
        { key: 'telephone', name: this.$t('전화'), value: orderData.telephone || 0 },
        { key: 'partner', name: this.$t('파트너'), value: orderData.partner || 0 },
        { key: 'mobile_table', name: this.$t('모바일테이블'), value: orderData.mobile_table || 0 },
        { key: 'etc', name: this.$t('기타'), value: orderData.etc || 0 },
      ]

      const total = items.reduce((sum, item) => sum + item.value, 0)
      this.orderTpList = items
        .filter((item) => item.value > 0)
        .map((item, index) => {
          const row = {
            ...item,
            color: this.chartColors[index % this.chartColors.length],
            percent: total > 0 ? ((item.value / total) * 100).toFixed(1) : 0,
            compareValue: compareOrderData[item.key] || 0,
          }
          // 배달 행은 배달앱(플랫폼 표의 그룹, 표에 없으면 기타)별로, 가게가 2곳 이상인 앱은 가게별로 더 펼친다 — 계산은
          // `utils/deliveryShops.deliveryOrderTypeRows` 한 곳(병기 지표·가게 구분 없음 포함)
          if (item.key === 'delivery_app') {
            const children = deliveryOrderTypeRows({
              current: { deliveryTps: this.deliveryTps, deliveryShops: this.deliveryShops },
              compare: this.compareData,
              total,
              colors: this.chartColors,
            })
            if (children.length) row.children = children
          }
          return row
        })
    },
    buildSalesTpList() {
      const salesData = this.salesTps[0] || {}
      const compareSalesData = this.compareData?.salesSums?.[0] || {}

      const items = [
        { key: 'cash_amount', name: this.$t('현금'), pick: (d) => d.cash_amount || 0 },
        { key: 'card_amount', name: this.$t('카드'), pick: (d) => d.card_amount || 0 },
        { key: 'easy_amount', name: this.$t('간편'), pick: (d) => d.easy_amount || 0 },
        { key: 'online_amount', name: this.$t('온라인'), pick: (d) => d.online_amount || 0 },
        { key: 'delivery_app_amount', name: this.$t('배달앱'), pick: (d) => d.delivery_app_amount || 0 },
        { key: 'etc_amount', name: this.$t('기타'), pick: (d) => d.etc_amount || 0 },
      ].map(({ key, name, pick }) => ({
        key,
        name,
        value: pick(salesData),
        compareValue: pick(compareSalesData),
      }))

      const total = items.reduce((sum, item) => sum + item.value, 0)
      this.salesTpList = items
        .filter((item) => item.value > 0)
        .map((item, index) => ({
          ...item,
          color: this.chartColors[index % this.chartColors.length],
          percent: total > 0 ? ((item.value / total) * 100).toFixed(1) : 0,
        }))
    },
    initCharts() {
      this.initOrderTypeChart()
      this.initPaymentTypeChart()
      this.initPeriodChart()
    },
    initOrderTypeChart() {
      if (!this.$refs.orderTypeChart) return
      if (this.orderTypeChart) this.orderTypeChart.dispose()

      const chartDom = this.$refs.orderTypeChart
      this.orderTypeChart = window.echarts.init(chartDom)

      const chartData = this.orderTpList.map((item) => ({
        value: item.value,
        name: item.name,
        itemStyle: { color: item.color },
      }))

      const option = {
        tooltip: {
          trigger: 'item',
          formatter: (params) => `${params.name}: ${this.formatCurrency(params.value)} (${params.percent}%)`,
        },
        legend: {
          orient: 'horizontal',
          bottom: 0,
          type: 'scroll',
          textStyle: { fontSize: 11 },
        },
        series: [
          {
            type: 'pie',
            radius: ['45%', '70%'],
            center: ['50%', '45%'],
            avoidLabelOverlap: false,
            itemStyle: { borderRadius: 6, borderColor: '#fff', borderWidth: 2 },
            label: { show: false },
            emphasis: {
              scale: true,
              scaleSize: 8,
              label: { show: true, fontSize: 14, fontWeight: 'bold' },
            },
            labelLine: { show: false },
            data: chartData,
          },
        ],
      }

      this.orderTypeChart.setOption(option)
    },
    initPaymentTypeChart() {
      if (!this.$refs.paymentTypeChart) return
      if (this.paymentTypeChart) this.paymentTypeChart.dispose()

      const chartDom = this.$refs.paymentTypeChart
      this.paymentTypeChart = window.echarts.init(chartDom)

      const chartData = this.salesTpList.map((item) => ({
        value: item.value,
        name: item.name,
        itemStyle: { color: item.color },
      }))

      const option = {
        tooltip: {
          trigger: 'item',
          formatter: (params) => `${params.name}: ${this.formatCurrency(params.value)} (${params.percent}%)`,
        },
        legend: {
          orient: 'horizontal',
          bottom: 0,
          type: 'scroll',
          textStyle: { fontSize: 11 },
        },
        series: [
          {
            type: 'pie',
            radius: ['45%', '70%'],
            center: ['50%', '45%'],
            avoidLabelOverlap: false,
            itemStyle: { borderRadius: 6, borderColor: '#fff', borderWidth: 2 },
            label: { show: false },
            emphasis: {
              scale: true,
              scaleSize: 8,
              label: { show: true, fontSize: 14, fontWeight: 'bold' },
            },
            labelLine: { show: false },
            data: chartData,
          },
        ],
      }

      this.paymentTypeChart.setOption(option)
    },
    initPeriodChart() {
      if (!this.$refs.periodChart) return
      if (this.periodChart) this.periodChart.dispose()

      const chartDom = this.$refs.periodChart
      this.periodChart = window.echarts.init(chartDom)

      const periodData = this.periods[0] || {}
      const categories = [this.$t('오늘'), this.$t('어제'), this.$t('이번주'), this.$t('이번달'), this.$t('지난달')]
      const types = ['TODAY', 'YESTERDAY', 'THIS_WEEK', 'THIS_MONTH', 'LAST_MONTH']
      const values = [
        periodData.today,
        periodData.yesterday,
        periodData.this_week,
        periodData.this_month,
        periodData.last_month,
      ]

      const option = {
        tooltip: {
          trigger: 'axis',
          axisPointer: { type: 'shadow' },
          formatter: (params) => `${params[0].name}: ${this.formatCurrency(params[0].value)}`,
        },
        grid: { left: '3%', right: '4%', bottom: '3%', top: '15%', containLabel: true },
        xAxis: {
          type: 'category',
          data: categories,
          axisLabel: { fontSize: 11 },
          axisTick: { alignWithLabel: true },
        },
        yAxis: {
          type: 'value',
          axisLabel: {
            fontSize: 10,
            formatter: (value) => {
              if (value >= 1000000) return (value / 1000000).toFixed(1) + 'M'
              if (value >= 1000) return (value / 1000).toFixed(0) + 'K'
              return value
            },
          },
        },
        series: [
          {
            type: 'bar',
            barWidth: '50%',
            data: values.map((value, index) => ({
              value: value,
              itemStyle: {
                color:
                  !this.isCustomPeriod && this.searchForm.periodType === types[index] ? this.chartColors[0] : '#D5D7DE',
                borderRadius: [4, 4, 0, 0],
              },
            })),
          },
        ],
      }

      this.periodChart.setOption(option)
    },
    initGraphChart() {
      if (!this.$refs.graphChart) return
      if (this.graphChart) this.graphChart.dispose()

      const chartDom = this.$refs.graphChart
      this.graphChart = window.echarts.init(chartDom)

      const categories = this.graphData.map((item) => item.date)
      const currentValues = this.graphData.map((item) => item.amount || 0)

      const series = [
        {
          name: '조회 기간',
          type: 'line',
          smooth: true,
          symbol: 'circle',
          symbolSize: 6,
          lineStyle: { width: 3, color: this.chartColors[0] },
          itemStyle: { color: this.chartColors[0] },
          areaStyle: {
            color: {
              type: 'linear',
              x: 0,
              y: 0,
              x2: 0,
              y2: 1,
              colorStops: [
                { offset: 0, color: 'rgba(242, 116, 56, 0.3)' },
                { offset: 1, color: 'rgba(242, 116, 56, 0.05)' },
              ],
            },
          },
          data: currentValues,
        },
      ]

      // 비교 데이터가 있으면 추가
      if (this.compareMode && this.compareGraphData.length > 0) {
        const compareValues = this.compareGraphData.map((item) => item.amount || 0)
        series.push({
          name: '비교 기간',
          type: 'line',
          smooth: true,
          symbol: 'circle',
          symbolSize: 6,
          lineStyle: { width: 2, color: this.chartColors[7], type: 'dashed' },
          itemStyle: { color: this.chartColors[7] },
          data: compareValues,
        })
      }

      const option = {
        tooltip: {
          trigger: 'axis',
          formatter: (params) => {
            let result = params[0].name + '<br/>'
            params.forEach((param) => {
              result += `${param.marker} ${param.seriesName}: ${this.formatCurrency(param.value)}<br/>`
            })
            return result
          },
        },
        legend: {
          show: this.compareMode,
          bottom: 0,
          textStyle: { fontSize: 12 },
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: this.compareMode ? '12%' : '3%',
          top: '10%',
          containLabel: true,
        },
        xAxis: {
          type: 'category',
          boundaryGap: false,
          data: categories,
          axisLabel: {
            fontSize: 10,
            rotate: categories.length > 15 ? 45 : 0,
          },
        },
        yAxis: {
          type: 'value',
          axisLabel: {
            fontSize: 10,
            formatter: (value) => {
              if (value >= 1000000) return (value / 1000000).toFixed(1) + 'M'
              if (value >= 1000) return (value / 1000).toFixed(0) + 'K'
              return value
            },
          },
        },
        series: series,
      }

      this.graphChart.setOption(option)
    },
  },
}
</script>

<style scoped>
.dashboard {
  padding: 15px;
  background: var(--color-bg-page);
  min-height: calc(100vh - 120px);
}

/* 매출 요약 카드 */
.summary-cards {
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 15px;
  margin-bottom: 20px;
}

.summary-card {
  background: #fff;
  border-radius: var(--radius-md);
  padding: 20px;
  box-shadow: var(--shadow-card);
  transition: transform 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease, background 0.3s ease;
  border: 2px solid transparent;
  cursor: pointer;
}

.summary-card:hover {
  transform: translateY(-2px);
  box-shadow: var(--shadow-card-hover);
}

.summary-card.active {
  border-color: var(--color-primary);
  background: linear-gradient(135deg, var(--color-primary-light) 0%, #fff 100%);
}

.summary-card.filter-card {
  background: linear-gradient(135deg, var(--color-bg-page) 0%, #fff 100%);
}

.summary-card.filter-card.active {
  background: linear-gradient(135deg, var(--color-primary-light) 0%, #fff 100%);
  border-color: var(--color-primary);
}

.card-header {
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--color-text-regular);
  font-size: 14px;
  margin-bottom: 12px;
}

.card-header i {
  font-size: 18px;
  color: var(--color-primary);
}

.filter-card .card-header i {
  color: var(--color-primary-dark);
}

.card-value {
  font-size: 24px;
  font-weight: 700;
  color: var(--color-text-primary);
  margin-bottom: 8px;
}

.card-value.filter-icon {
  font-size: 20px;
  color: var(--color-text-secondary);
}

.card-footer {
  height: 20px;
}

.period-badge.selected {
  font-size: 12px;
  color: var(--color-primary);
  font-weight: 500;
}

/* 상세 조회 패널 */
.advanced-filter {
  background: #fff;
  border-radius: var(--radius-md);
  padding: 20px;
  margin-bottom: 20px;
  box-shadow: var(--shadow-card);
  border-left: 4px solid var(--color-primary);
}

.filter-group {
  margin-bottom: 15px;
}

.filter-label {
  font-size: 14px;
  color: var(--color-text-regular);
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  gap: 6px;
}

.filter-label i {
  color: var(--color-primary);
}

.compare-options {
  margin-top: 10px;
}

.compare-info {
  display: flex;
  gap: 30px;
  margin-top: 15px;
  padding: 15px;
  background: var(--color-bg-page);
  border-radius: 6px;
}

.compare-period {
  display: flex;
  align-items: center;
  gap: 10px;
}

.compare-period .label {
  font-size: 13px;
  color: var(--color-text-secondary);
}

.compare-period .value {
  font-size: 14px;
  font-weight: 600;
  color: var(--color-text-primary);
}

.compare-period.current .value {
  color: var(--color-primary);
}

.compare-period.compare .value {
  color: var(--color-text-secondary);
}

.filter-actions {
  margin-top: 20px;
  display: flex;
  gap: 10px;
  justify-content: flex-end;
}

/* 슬라이드 애니메이션 */
.slide-enter-active,
.slide-leave-active {
  transition: opacity 0.3s ease, transform 0.3s ease;
}

.slide-enter,
.slide-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

/* 비교 결과 요약 */
.compare-summary {
  margin-bottom: 20px;
}

.compare-card {
  background: #fff;
  border-radius: var(--radius-md);
  padding: 20px;
  box-shadow: var(--shadow-card);
  text-align: center;
  margin-bottom: 15px;
}

.compare-card-header {
  font-size: 14px;
  color: var(--color-text-secondary);
  margin-bottom: 15px;
}

.compare-card-body .current-value {
  font-size: 28px;
  font-weight: 700;
  color: var(--color-text-primary);
  margin-bottom: 8px;
}

.compare-card-body .compare-value {
  font-size: 14px;
  color: var(--color-text-secondary);
  margin-bottom: 8px;
}

.compare-card-body .compare-value .label {
  margin-right: 5px;
}

.compare-card-body .diff-value {
  font-size: 16px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
}

.diff-value.positive {
  color: var(--color-success);
}

.diff-value.negative {
  color: var(--color-danger);
}

.diff-value.neutral {
  color: var(--color-text-secondary);
}

/* 그래프 섹션 */
.graph-section {
  margin-bottom: 20px;
}

.graph-container {
  width: 100%;
  height: 350px;
}

/* 차트 카드 */
.chart-section {
  margin-bottom: 20px;
}

.chart-card {
  background: #fff;
  border-radius: var(--radius-md);
  padding: 20px;
  box-shadow: var(--shadow-card);
  margin-bottom: 15px;
}

.chart-header {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 16px;
  font-weight: 600;
  color: var(--color-text-primary);
  margin-bottom: 15px;
  padding-bottom: 10px;
  border-bottom: 1px solid var(--color-border);
}

.chart-header i {
  font-size: 18px;
  color: var(--color-primary);
}

.chart-header .period-info {
  font-size: 12px;
  color: var(--color-text-secondary);
  font-weight: normal;
  margin-left: auto;
}

.chart-container {
  width: 100%;
  height: 280px;
}

/* 테이블 카드 */
.table-section {
  margin-bottom: 20px;
}

.table-card {
  background: #fff;
  border-radius: var(--radius-md);
  padding: 20px;
  box-shadow: var(--shadow-card);
  margin-bottom: 15px;
}

.table-header {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 16px;
  font-weight: 600;
  color: var(--color-text-primary);
  margin-bottom: 15px;
  padding-bottom: 10px;
  border-bottom: 1px solid var(--color-border);
}

.table-header .header-basis-info {
  margin-left: auto;
  font-weight: 400;
}

.settled-ref {
  font-size: 11px;
  color: var(--color-text-secondary);
  line-height: 1.4;
}

.table-header i {
  font-size: 18px;
  color: var(--color-primary);
}

.type-badge {
  display: inline-block;
  padding: 4px 10px;
  border-radius: 4px;
  color: #fff;
  font-size: 12px;
  font-weight: 500;
}

.shop-name {
  color: var(--color-text-primary);
  font-size: 12px;
}

.shop-name--unknown {
  color: var(--color-text-secondary);
}

.positive {
  color: var(--color-success);
}

.negative {
  color: var(--color-danger);
}

.neutral {
  color: var(--color-text-secondary);
}

/* 랭킹 카드 */
.ranking-section {
  margin-bottom: 20px;
}

.ranking-card {
  background: #fff;
  border-radius: var(--radius-md);
  padding: 20px;
  box-shadow: var(--shadow-card);
  margin-bottom: 15px;
}

.ranking-header {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 16px;
  font-weight: 600;
  color: var(--color-text-primary);
  margin-bottom: 15px;
  padding-bottom: 10px;
  border-bottom: 1px solid var(--color-border);
}

.ranking-header i {
  font-size: 18px;
  color: var(--color-primary);
}

.ranking-list {
  max-height: 350px;
  overflow-y: auto;
}

.ranking-item {
  display: flex;
  align-items: center;
  padding: 10px 0;
  border-bottom: 1px solid var(--color-border-light);
}

.ranking-item:last-child {
  border-bottom: none;
}

.ranking-item .rank {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: var(--color-border);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 600;
  color: var(--color-text-regular);
  margin-right: 12px;
  flex-shrink: 0;
}

.ranking-item .rank.gold {
  background: linear-gradient(135deg, #ffd700, #ffb800);
  color: #fff;
}

.ranking-item .rank.silver {
  background: linear-gradient(135deg, #c0c0c0, #a0a0a0);
  color: #fff;
}

.ranking-item .rank.bronze {
  background: linear-gradient(135deg, #cd7f32, #b06c2a);
  color: #fff;
}

.ranking-item .name {
  flex: 1;
  font-size: 14px;
  color: var(--color-text-primary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ranking-item .count {
  font-size: 12px;
  color: var(--color-text-secondary);
  margin-right: 15px;
  flex-shrink: 0;
}

.ranking-item .amount {
  font-size: 14px;
  font-weight: 600;
  color: var(--color-primary);
  flex-shrink: 0;
}

.empty-ranking {
  text-align: center;
  color: var(--color-text-secondary);
  padding: 40px 0;
}

/* 반응형 */
@media (max-width: 1400px) {
  .summary-cards {
    grid-template-columns: repeat(3, 1fr);
  }
}

@media (max-width: 992px) {
  .summary-cards {
    grid-template-columns: repeat(2, 1fr);
  }

  .compare-info {
    flex-direction: column;
    gap: 10px;
  }
}

@media (max-width: 768px) {
  .dashboard {
    padding: 10px;
  }

  .summary-cards {
    grid-template-columns: repeat(2, 1fr);
  }

  .card-value {
    font-size: 18px;
  }

  .chart-container {
    height: 220px;
  }

  .graph-container {
    height: 280px;
  }

  .ranking-item .amount {
    font-size: 12px;
  }

  .compare-card-body .current-value {
    font-size: 22px;
  }
}

@media (max-width: 480px) {
  .summary-cards {
    grid-template-columns: 1fr;
  }
}
</style>
